import { useRef } from 'react';
import {
	ChevronLeft,
	ChevronRight,
	ChevronsRight,
	FileText,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import {
	PRONTUARIO_GHOST_BUTTON_CLASS,
	PRONTUARIO_HEADER_ICON_SLOT_CLASS,
	PRONTUARIO_HEADER_PADDING_X,
	PRONTUARIO_PILL_BUTTON_CLASS,
} from '../../constants/prontuarioTheme';
import { SessionCallProntuarioAudioVisualizer } from './SessionCallProntuarioAudioVisualizer';
import { SessionCallProntuarioHintBalloon } from './SessionCallProntuarioHintBalloon';
// import { SessionCallProntuarioTranscriptionMenu } from './SessionCallProntuarioTranscriptionMenu';

type ProntuarioHeaderLeftAction =
	| 'sessions-shortcut'
	| 'none'
	| 'sessions-back'
	| 'notes-back'
	| 'minimize';

type SessionCallProntuarioHeaderProps = {
	leftAction?: ProntuarioHeaderLeftAction;
	onBack?: () => void;
	onOpenSessions?: () => void;
	onMinimize?: () => void;
	showRecordingHint?: boolean;
	recordingHintMessage?: string;
	recordingHintKey?: number;
	transcriptionActive?: boolean;
	isProntuarioLiveCaptureEnabled?: boolean;
	onDismissRecordingHint?: () => void;
	onOpenTranscriptionHistory?: () => void;
	onClearTranscriptionConversation?: () => void;
	onStopProntuarioLiveCapture?: () => void;
	onResumeProntuarioLiveCapture?: () => void;
	isTranscriptionHistoryOpen?: boolean;
	isLeftActionDisabled?: boolean;
	className?: string;
	tutorialDemoTarget?: string;
	tutorialDemoSessionsOpen?: string;
	tutorialDemoMinimize?: string;
	hideInlineTranscription?: boolean;
};

const SessionCallProntuarioHeader = ({
	leftAction = 'sessions-shortcut',
	onBack,
	onOpenSessions,
	onMinimize,
	showRecordingHint = false,
	recordingHintMessage = 'Toque na gravação para abrir a transcrição em tempo real.',
	recordingHintKey,
	transcriptionActive = true,
	isProntuarioLiveCaptureEnabled: _isProntuarioLiveCaptureEnabled = true,
	onDismissRecordingHint,
	onOpenTranscriptionHistory,
	onClearTranscriptionConversation,
	onStopProntuarioLiveCapture,
	onResumeProntuarioLiveCapture: _onResumeProntuarioLiveCapture,
	isTranscriptionHistoryOpen = false,
	isLeftActionDisabled = false,
	className,
	tutorialDemoTarget,
	tutorialDemoSessionsOpen,
	tutorialDemoMinimize,
	hideInlineTranscription = false,
}: SessionCallProntuarioHeaderProps) => {
	const transcriptionAnchorRef = useRef<HTMLDivElement>(null);

	const openTranscriptionHistory = () => {
		onDismissRecordingHint?.();
		onOpenTranscriptionHistory?.();
	};

	const leftActionDisabledClass =
		'cursor-not-allowed opacity-40 hover:bg-transparent hover:text-slate-400 active:bg-transparent';

	const renderLeftAction = () => {
		switch (leftAction) {
			case 'sessions-shortcut':
				return (
					<button
						type="button"
						onClick={onOpenSessions}
						disabled={isLeftActionDisabled}
						{...(tutorialDemoSessionsOpen
							? { 'data-tutorial-demo': tutorialDemoSessionsOpen }
							: {})}
						className={cn(
							'flex h-9 items-center rounded-full pr-2.5',
							PRONTUARIO_PILL_BUTTON_CLASS,
							isLeftActionDisabled && leftActionDisabledClass,
						)}
						aria-label="Ver sessões anteriores"
					>
						<span className={PRONTUARIO_HEADER_ICON_SLOT_CLASS}>
							<ChevronsRight
								className="size-4 shrink-0"
								strokeWidth={2.25}
							/>
						</span>
						<span className="-ml-1 flex items-center">
							<FileText
								className="size-4 shrink-0"
								strokeWidth={2}
							/>
						</span>
					</button>
				);

			case 'sessions-back':
				return (
					<button
						type="button"
						onClick={onBack}
						disabled={isLeftActionDisabled}
						className={cn(
							'flex h-9 items-center rounded-full pr-2.5',
							PRONTUARIO_PILL_BUTTON_CLASS,
							isLeftActionDisabled && leftActionDisabledClass,
						)}
						aria-label="Voltar para sessões"
					>
						<span className={PRONTUARIO_HEADER_ICON_SLOT_CLASS}>
							<ChevronRight
								className="size-4 shrink-0"
								strokeWidth={2.25}
							/>
						</span>
						<span className="-ml-1 flex items-center">
							<FileText
								className="size-4 shrink-0"
								strokeWidth={2}
							/>
						</span>
					</button>
				);

			case 'notes-back':
				return (
					<button
						type="button"
						onClick={onBack}
						disabled={isLeftActionDisabled}
						className={cn(
							PRONTUARIO_HEADER_ICON_SLOT_CLASS,
							'rounded-full',
							PRONTUARIO_GHOST_BUTTON_CLASS,
							isLeftActionDisabled && leftActionDisabledClass,
						)}
						aria-label="Voltar para sessão atual"
					>
						<ChevronLeft
							className="size-4 shrink-0"
							strokeWidth={2.25}
						/>
					</button>
				);

			case 'minimize':
				return (
					<button
						type="button"
						onClick={() => onMinimize?.()}
						disabled={isLeftActionDisabled}
						{...(tutorialDemoMinimize
							? { 'data-tutorial-demo': tutorialDemoMinimize }
							: {})}
						className={cn(
							PRONTUARIO_HEADER_ICON_SLOT_CLASS,
							'rounded-full',
							PRONTUARIO_GHOST_BUTTON_CLASS,
							isLeftActionDisabled && leftActionDisabledClass,
						)}
						aria-label="Minimizar prontuário"
					>
						<ChevronsRight
							className="size-4 shrink-0"
							strokeWidth={2.25}
						/>
					</button>
				);

			default:
				return (
					<span
						className={PRONTUARIO_HEADER_ICON_SLOT_CLASS}
						aria-hidden
					/>
				);
		}
	};

	const showTranscriptionControls =
		!hideInlineTranscription &&
		transcriptionActive &&
		(onOpenTranscriptionHistory != null ||
			onClearTranscriptionConversation != null ||
			onStopProntuarioLiveCapture != null);

	const isTranscriptionViewOpen =
		isTranscriptionHistoryOpen && showTranscriptionControls;

	return (
		<header
			{...(tutorialDemoTarget
				? { 'data-tutorial-demo': tutorialDemoTarget }
				: {})}
			className={cn(
				'relative z-20 grid shrink-0 items-center overflow-visible py-2',
				PRONTUARIO_HEADER_PADDING_X,
				isTranscriptionViewOpen
					? 'grid-cols-1 justify-items-center'
					: 'grid-cols-[1fr_auto_1fr]',
				className,
			)}
		>
			{!isTranscriptionViewOpen ? (
				<div className="flex min-h-9 items-center justify-start">
					<AnimatePresence mode="wait" initial={false}>
						<motion.div
							key={leftAction}
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.12, ease: 'easeOut' }}
							className="flex items-center"
						>
							{renderLeftAction()}
						</motion.div>
					</AnimatePresence>
				</div>
			) : null}

			<div className="relative flex min-h-9 items-center justify-center">
				{showRecordingHint && !isTranscriptionViewOpen ? (
					<SessionCallProntuarioHintBalloon
						key={recordingHintKey}
						id="prontuario-header-recording-hint"
						message={recordingHintMessage}
						onDismiss={() => onDismissRecordingHint?.()}
						placement="below"
						align="center"
						widthClassName="w-[min(240px,calc(100vw-2rem))]"
					/>
				) : null}
				{showTranscriptionControls ? (
					<div
						ref={transcriptionAnchorRef}
						className="inline-flex shrink-0"
					>
						<SessionCallProntuarioAudioVisualizer
							size="large"
							animated={transcriptionActive}
							interactive
							toggleOpen={isTranscriptionHistoryOpen}
							onBarsClick={openTranscriptionHistory}
							chevronAction={
								<svg
									className={cn(
										'size-3.5 shrink-0 text-[#8ab4f8]/85 transition-transform duration-200',
										isTranscriptionHistoryOpen &&
											'rotate-180',
									)}
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth={2.25}
									strokeLinecap="round"
									strokeLinejoin="round"
									aria-hidden
								>
									<path d="m6 9 6 6 6-6" />
								</svg>
							}
							ariaLabel={
								isTranscriptionHistoryOpen
									? 'Fechar transcrição ao vivo no prontuário'
									: 'Abrir transcrição ao vivo no prontuário'
							}
							className={cn(
								'ring-2 ring-inset',
								showRecordingHint || isTranscriptionHistoryOpen
									? 'ring-[#8ab4f8]/35'
									: 'ring-transparent',
							)}
						/>
					</div>
				) : hideInlineTranscription ? null : (
					<SessionCallProntuarioAudioVisualizer
						size="large"
						animated={transcriptionActive}
						decorative
					/>
				)}
			</div>

			{!isTranscriptionViewOpen ? (
				<div className="min-h-9" aria-hidden />
			) : null}
		</header>
	);
};

export { SessionCallProntuarioHeader };
